import { RotateCcw, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import type { ToolNameView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { ARCHIVED_TONE } from '../../../components/status-tones';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { formatDateTimeLong, formatNumber, formatRelative } from '../../../lib/format';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useAdminToolNames, useChangeToolName } from '../../tool-names/api/tool-names-api';
import styles from './SettingsPages.module.css';
import own from './ToolNamesPage.module.css';

/**
 * ADMIN-19 / F19.14 (owner's evening review 2026-11-01, Request a hire item 7; DECISIONS-LOG 2026-11-02 Q4 a): the tools box 1
 * of the Ideal candidate profile has learned from what requesters typed. HR and admins delete a misspelt one (it is hidden, and
 * never learned again) or merge it into the right one. Requests nobody has approved yet, and draft jobs, are rewritten to the
 * right spelling; anything approved keeps what was written.
 */
export function ToolNamesPage(): ReactNode {
  const canView = useCanView('hiring_setup');
  const names = useAdminToolNames();
  if (!canView) return <ForbiddenState resource="the tools list" contact="Your admin" />;
  if (names.isPending) return <SkeletonLines count={10} />;
  if (names.error) return <ServerErrorState onRetry={() => void names.refetch()} />;

  const onList = names.data.filter((entry) => !entry.isHidden);
  const offList = names.data.filter((entry) => entry.isHidden);
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Tools and systems</h1>
        <p className={styles.lead}>
          What box 1 of the ideal candidate profile offers. A tool typed on a request is added here, so the next person can pick it. Delete a misspelt one, or
          merge it into the right name: requests not yet approved and draft jobs change to the right name, approved ones keep what was written.
        </p>
      </header>
      <Card title="On the list" meta={`${onList.length} in all`}>
        {onList.length === 0 ? (
          <EmptyState isInset title="No tools yet" body="They appear here once someone names a tool on a request." action={null} />
        ) : (
          <ul className={styles.rows}>
            {onList.map((entry) => (
              <ToolNameLine key={entry.id} entry={entry} others={onList} />
            ))}
          </ul>
        )}
      </Card>
      {offList.length > 0 && (
        <Card title="Off the list" meta={`${offList.length} deleted or merged`}>
          <ul className={styles.rows}>
            {offList.map((entry) => (
              <ToolNameLine key={entry.id} entry={entry} others={onList} />
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function usedIn(entry: ToolNameView): string {
  const requests = `${formatNumber(entry.requests)} ${entry.requests === 1 ? 'request' : 'requests'}`;
  const jobs = `${formatNumber(entry.jobs)} ${entry.jobs === 1 ? 'job' : 'jobs'}`;
  return `Used in ${requests} · ${jobs}`;
}

function ToolNameLine({ entry, others }: { readonly entry: ToolNameView; readonly others: readonly ToolNameView[] }): ReactNode {
  const canEdit = useCanEdit('hiring_setup');
  const change = useChangeToolName();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const targets = others.filter((other) => other.id !== entry.id).map((other) => ({ value: other.id, label: other.name }));

  const refused = (cause: unknown): void => {
    show({ tone: 'danger', message: "The list wasn't changed", detail: errorMessage(cause) });
  };

  function remove(): void {
    change.mutate(
      { kind: 'hide', entry },
      {
        onSuccess: ({ toolName }) => {
          showUndo(`"${entry.name}" deleted from the list`, () => change.mutate({ kind: 'restore', entry: toolName }, { onError: refused }));
        },
        onError: refused,
      },
    );
  }

  function merge(intoId: string): void {
    const into = others.find((other) => other.id === intoId);
    if (!into) return;
    change.mutate(
      { kind: 'merge', entry, into },
      { onSuccess: () => show({ tone: 'success', message: `"${entry.name}" merged into "${into.name}"` }), onError: refused },
    );
  }

  function putBack(): void {
    change.mutate({ kind: 'restore', entry }, { onSuccess: () => show({ tone: 'success', message: `"${entry.name}" is back on the list` }), onError: refused });
  }

  return (
    <li className={entry.isHidden ? styles.itemMuted : styles.item}>
      <div className={styles.cell}>
        <span className={styles.name}>
          {entry.name}
          {entry.mergedIntoName !== null && (
            <Chip tone={ARCHIVED_TONE} plain>
              Merged into {entry.mergedIntoName}
            </Chip>
          )}
          {entry.isHidden && entry.mergedIntoName === null && (
            <Chip tone={ARCHIVED_TONE} plain>
              Deleted
            </Chip>
          )}
        </span>
        <span className={styles.meta}>
          {usedIn(entry)}
          {' · added '}
          {entry.createdByName === null ? '' : `by ${entry.createdByName} `}
          <span title={formatDateTimeLong(entry.createdAt)}>{formatRelative(entry.createdAt)}</span>
        </span>
      </div>
      {canEdit && (
        <div className={styles.controls}>
          {entry.isHidden ? (
            <Button size="sm" variant="ghost" icon={RotateCcw} onClick={putBack}>
              Put back
            </Button>
          ) : (
            <>
              {targets.length > 0 && (
                <span className={own.mergePick}>
                  <Select label={`Merge ${entry.name} into`} placeholder="Merge into…" value={null} options={targets} onChange={merge} />
                </span>
              )}
              <Button size="sm" variant="ghost" icon={Trash2} onClick={remove}>
                Delete
              </Button>
            </>
          )}
        </div>
      )}
    </li>
  );
}
